import { useInfiniteQuery, useQuery } from 'react-query';
import { useAuth } from '../auth/AuthProvider';
import { InsightDashboard } from '../utils/interfaces';
import { getRequestHeaders, vake_api_server_url } from './helpers';

type PropsGetVesselsQuery = {
    data: ActorModeVessel[];
    nextPage: number | undefined;
};
export const useGetVesselsQuery = (
    uuid: string,
    startTime: Date,
    endTime: Date,
    verifiedActive: boolean,
    darkActive: boolean,
    unCorrelatedActive: boolean,
    sanctionedActive: boolean
) => {
    const { token } = useAuth();
    // By adding noMatchingLabelActive number of DB requests are reduced.
    // Even better will be to have separate query for each of them.
    const noMatchingLabelActive = !(verifiedActive || darkActive || unCorrelatedActive);
    return useInfiniteQuery<PropsGetVesselsQuery>({
        queryKey: [
            'queryKeyUseGetVesselsQuery',
            uuid,
            startTime,
            endTime,
            verifiedActive || noMatchingLabelActive,
            darkActive || noMatchingLabelActive,
            unCorrelatedActive || noMatchingLabelActive,
            sanctionedActive,
        ],
        queryFn: async ({ pageParam = 0 }) => {
            const limit = 10;
            const skip = pageParam * limit;
            let url = `${vake_api_server_url}/actor-mode/all/${uuid}`;
            url += `?start=${startTime.toISOString()}`;
            url += `&end=${endTime.toISOString()}`;
            url += `&limit=${limit}`;
            url += `&skip=${skip}`;
            url += `&verified_active=${verifiedActive}`;
            url += `&dark_active=${darkActive}`;
            url += `&un_correlated_active=${verifiedActive}`;
            url += `&sanctioned_active=${sanctionedActive}`;
            const res = await fetch(url, {
                credentials: 'include',
                headers: getRequestHeaders(token),
            });
            if (!res.ok) {
                throw new Error(res.statusText);
            }
            if (res.status === 204) {
                return { data: [], nextPage: undefined };
            }
            const data = await res.json();
            const hasNextPage = data.length === limit;
            return { data, nextPage: hasNextPage ? pageParam + 1 : undefined };
        },
        getNextPageParam: (lastPage) => lastPage.nextPage,
    });
};

type ReportInfoApiResponse = {
    description: string;
    startTime: string | null;
    endTime: string | null;
};
const queryKeyUseGetReportInfo = (uuid: string) => ['queryKeyUseGetReportInfo', uuid];
export const useGetReportInfo = (uuid: string) => {
    const { token } = useAuth();
    const url = `${vake_api_server_url}/report/overwatch/${uuid}/info`;
    return useQuery<ReportInfo>(queryKeyUseGetReportInfo(uuid), async () => {
        const res = await fetch(url, {
            credentials: 'include',
            headers: getRequestHeaders(token),
        });
        if (!res.ok) {
            throw new Error(res.statusText);
        }
        const apiData: ReportInfoApiResponse = await res.json();
        return {
            description: apiData.description,
            startTime: apiData.startTime === null ? null : new Date(apiData.startTime),
            endTime: apiData.endTime === null ? null : new Date(apiData.endTime),
        };
    });
};

const queryKeyUseGetVesselQuery = (insightUuid: string) => ['queryKeyUseGetVesselQuery', insightUuid];
export const useGetVesselQuery = (insightUuid: string) => {
    const { token } = useAuth();
    const url = `${vake_api_server_url}/actor-mode/vessel/${insightUuid}`;
    return useQuery<ActorModeVessel>(
        queryKeyUseGetVesselQuery(insightUuid),
        async () => {
            const res = await fetch(url, {
                credentials: 'include',
                headers: getRequestHeaders(token),
            });
            if (!res.ok) {
                throw new Error(res.statusText);
            }
            return await res.json();
        },
        { enabled: insightUuid.length > 0 }
    );
};

const queryKeyUseGetVesselTagsQuery = (insightUuid: string) => ['queryKeyUseGetVesselTagsQuery', insightUuid];
export const useGetVesselTagsQuery = (insightUuid: string) => {
    const { token } = useAuth();
    const url = `${vake_api_server_url}/actor-mode/vessel/${insightUuid}/actions`;
    return useQuery<string[]>(
        queryKeyUseGetVesselTagsQuery(insightUuid),
        async () => {
            const res = await fetch(url, {
                credentials: 'include',
                headers: getRequestHeaders(token),
            });
            if (!res.ok) {
                throw new Error(res.statusText);
            }
            return await res.json();
        },
        { enabled: insightUuid.length > 0 }
    );
};

const queryKeyUseGetVesselPathfinderQuery = (mmsi: number | null) => ['queryKeyUseGetVesselPathfinderQuery', mmsi];
export const useGetVesselPathfinderQuery = (mmsi: number | null) => {
    const { token } = useAuth();
    const url = `${vake_api_server_url}/actor-mode/vessel/${mmsi}/pathfinder`;
    return useQuery<Pathfinder[]>(
        queryKeyUseGetVesselPathfinderQuery(mmsi),
        async () => {
            const res = await fetch(url, {
                credentials: 'include',
                headers: getRequestHeaders(token),
            });
            if (!res.ok) {
                throw new Error(res.statusText);
            }
            return await res.json();
        },
        { enabled: mmsi !== null }
    );
};

const queryKeyUseGetInsights = (mmsi: number | null, inside: boolean, uuid: string, insightUuid: string) => [
    'queryKeyUseGetInsights',
    mmsi,
    inside,
    uuid,
    insightUuid,
];
export const useGetInsights = (
    mmsi: number | null,
    enabled: boolean,
    inside: boolean,
    uuid: string,
    insightUuid: string
) => {
    const { token } = useAuth();
    let url = `${vake_api_server_url}/actor-mode/something?inside=${inside}&uuid=${uuid}&insight_uuid=${insightUuid}`;
    if (mmsi !== null) {
        url = `${url}&mmsi=${mmsi}`;
    }
    return useQuery<InsightDashboard[]>(
        queryKeyUseGetInsights(mmsi, inside, uuid, insightUuid),
        async () => {
            const res = await fetch(url, {
                credentials: 'include',
                headers: getRequestHeaders(token),
            });
            if (!res.ok) {
                throw new Error(res.statusText);
            }
            return await res.json();
        },
        { enabled: enabled && (mmsi !== null || !!insightUuid) }
    );
};

const queryKeyUseGetNewestInsight = (insightUuid: string) => ['queryKeyUseGetNewestInsight', insightUuid];
export const useGetNewestInsight = (insightUuid: string) => {
    const { token } = useAuth();
    const url = `${vake_api_server_url}/actor-mode/vessel/${insightUuid}/newest-insight`;
    return useQuery<InsightDashboard>(
        queryKeyUseGetNewestInsight(insightUuid),
        async () => {
            const res = await fetch(url, {
                credentials: 'include',
                headers: getRequestHeaders(token),
            });
            if (!res.ok) {
                throw new Error(res.statusText);
            }
            return await res.json();
        },
        { enabled: insightUuid.length > 0 }
    );
};
